<template>
  <div class="alb-Wrapper">
    <slot
      v-for="(activity, index) in activitiesToShow"
      :key="activity.id || index"
      :activity="activity"
      :get-activity-time="calculateActivityTime(activity.date)"
      name="activity"
    />
    <InfiniteScrollLoader :identifier="infiniteId" @infinite="infiniteHandler">
      <template #no-results>
        <slot name="no-results" />
      </template>
      <template #loader>
        <slot name="loader" />
      </template>
    </InfiniteScrollLoader>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ObjectivesInfoApiHandler from '@/api/okr-elements'
import { ACTIVITY_LOG, PROGRESS_HISTORY } from '@/utils/activities'
import { getMonthList, getTimeDiffs } from '@/utils/date'
import { handleError } from '@/utils/error-handling'

import InfiniteScrollLoader from '@/components/ui/InfiniteScrollLoader/InfiniteScrollLoader'

defineOptions({
  name: 'ActivityListBase'
})

const props = defineProps({
  activityType: {
    type: String,
    default: ACTIVITY_LOG
  },

  objective: {
    type: Object,
    required: true
  },

  pageSize: {
    type: Number,
    default: 10
  }
})

const { t } = useI18n()
const activities = ref([])
const page = ref(0)
const infiniteId = ref(Date.now())

const activitiesToShow = computed(() => activities.value.slice(0, props.pageSize * page.value))

const refreshData = () => {
  page.value = 0
  activities.value = []
  infiniteId.value = Date.now()
}

const calculateActivityTime = item => {
  const editedOn = new Date(item)
  const now = new Date()

  const { diffForDay, diffForHour, diffForMin } = getTimeDiffs(now, editedOn)

  const formatHours = `0${editedOn.getHours()}`.slice(-2)
  const formatMins = `0${editedOn.getMinutes()}`.slice(-2)

  if (diffForDay >= 365) {
    return `${
      getMonthList()[editedOn.getMonth()]
    } ${editedOn.getDate()}, ${editedOn.getFullYear()} ${formatHours}:${formatMins}`
  }

  if (diffForDay > 7) {
    return `${
      getMonthList()[editedOn.getMonth()]
    } ${editedOn.getDate()}, ${formatHours}:${formatMins}`
  }

  if (diffForHour >= 1 && diffForDay < 1) {
    return `${diffForHour} ${t('objective.hours_ago', diffForHour)}`
  }

  if (diffForHour < 1 && diffForMin >= 1) {
    return `${diffForMin} ${t('token.minutes_ago')}`
  }

  if (diffForMin < 1) {
    return t('common.just_now').toLowerCase()
  }

  return `${diffForDay} ${t('objectives.days_ago')}`
}

const getActivities = async () => {
  const api = new ObjectivesInfoApiHandler()

  try {
    let result = []
    if (props.activityType === ACTIVITY_LOG) {
      result = await api.getHistoryForObject({
        objectId: props.objective.id,
        objectTypeId: props.objective.typeId
      })
    } else if (props.activityType === PROGRESS_HISTORY) {
      result = await api.getProgressHistory({ elementId: props.objective.id })
    }
    activities.value = result
  } catch (error) {
    handleError({ error })
  }
}

const infiniteHandler = async $state => {
  if (page.value === 0) {
    await getActivities()
  }
  if (activitiesToShow.value.length === activities.value.length) {
    $state.complete()
  } else {
    page.value += 1
    $state.loaded()
  }
}

defineExpose({
  refreshData
})
</script>

<style scoped>
.alb-Wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
